import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { Paper, Typography, Box } from '@mui/material';
import sortBy from 'lodash/sortBy';

import { Loader } from 'ui';
import { LinkButton } from 'ui/Buttons';
import { addNotificationAction } from 'store/notifications/notificationsActions';

import { deleteSubscriptions, duplicateShopProducts } from '../../services';
import { fetchShopProductsAction, fetchShopProductPropertiesAction } from '../../store/shopActions';
import { ShopSelectionList } from '../../components/ShopSelectionList/ShopSelectionList';

const ShopPanelProductList = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const products = useSelector(({ shop }) => shop.products);

  const [selectedProducts, setSelectedProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const handleSelectionChange = items => {
    setSelectedProducts(items);
  };

  const actions = [
    {
      name: t('Duplicate'),
      action: () => {
        setIsLoading(true);

        duplicateShopProducts({ products: selectedProducts })
          .then(() => {
            dispatch(
              addNotificationAction({
                message: t('Product successfully duplicated'),
                variant: 'success',
              }),
            );
            dispatch(fetchShopProductsAction());
          })
          .catch(error => {
            if (error) {
              dispatch(
                addNotificationAction({
                  message: t('Cannot duplicate a product'),
                  variant: 'error',
                }),
              );
            }
          })
          .finally(() => {
            setIsLoading(false);
          });
      },
    },
    {
      name: t('Delete'),
      action: () => {
        setIsLoading(true);

        deleteSubscriptions({ products: selectedProducts })
          .then(() => {
            dispatch(
              addNotificationAction({
                message: t('Product successfully deleted'),
                variant: 'success',
              }),
            );
            dispatch(fetchShopProductsAction());
          })
          .catch(error => {
            if (error) {
              dispatch(
                addNotificationAction({
                  message: t('Cannot delete a product'),
                  variant: 'error',
                }),
              );
            }
          })
          .finally(() => {
            setIsLoading(false);
          });
      },
    },
  ];

  useEffect(() => {
    dispatch(fetchShopProductPropertiesAction());
    dispatch(fetchShopProductsAction());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Box mb={2}>
        <Typography variant="h5">{t('Product list')}</Typography>
      </Box>

      <Paper elevation={3}>
        {isLoading || products.isPending ? (
          <Box py={4}>
            <Loader center />
          </Box>
        ) : (
          <ShopSelectionList
            actions={actions}
            products={sortBy(products.data, 'name')}
            onSelectionChange={handleSelectionChange}
            toolbar={
              <LinkButton
                to="/subscriptions/panel/create"
                label={t('Create product')}
                color="primary"
                variant="contained"
                size="small"
              />
            }
            columns={[
              'name',
              'category',
              'region',
              'segment',
              'productLines',
              'industry',
              'source',
              'pricing',
              'validity',
              'amount',
              'active',
            ]}
            editMode
          />
        )}
      </Paper>
    </>
  );
};

export { ShopPanelProductList };
